import React, { useMemo } from 'react';

import { Flex, Image, Skeleton } from 'ui';

import { useShowCause, useUserCause } from '../../../api/core';
import { HandHeartIcon } from '../assets/images/HandHeart';
import classes from './CauseLogo.module.css';

type Props = {
  cause_uuid?: string;
  styles?: React.CSSProperties;
  size?: 'lg' | 'md' | 'sm';
};

const containerSizes = {
  lg: {
    width: 90,
    height: 90,
  },
  md: {
    width: 72,
    height: 72,
  },
  sm: {
    width: 48,
    height: 48,
  },
};
const imageSizes = {
  lg: {
    width: '90px',
  },
  md: {
    width: '72px',
  },
  sm: {
    width: '48px',
  },
};
const iconSizes = {
  lg: {
    width: 80,
    height: 64,
    top: '-1px',
    left: '18px',
  },
  md: {
    width: 64,
    height: 51,
    top: '-1px',
    left: '14px',
  },
  sm: {
    width: 42,
    height: 33,
    top: '-1px',
    left: '10px',
  },
};

export const CauseLogo = ({ cause_uuid, styles, size = 'md' }: Props) => {
  const { data: userCause, isInitialLoading: isUserCauseLoading } = useUserCause();
  const { data: fetchedCause, isLoading: isCauseLoading } = useShowCause(cause_uuid);

  const { isLoading, cause } = useMemo(() => {
    const cause = cause_uuid ? fetchedCause : userCause;
    let isLoading = true;

    if (cause_uuid) {
      isLoading = isCauseLoading;
    } else {
      if (userCause || isUserCauseLoading) {
        isLoading = isUserCauseLoading;
      } else if (!cause_uuid && !userCause) {
        isLoading = false;
      }
    }
    return {
      isLoading,
      cause,
    };
  }, [cause_uuid, fetchedCause, userCause, isCauseLoading, isUserCauseLoading]);

  const skeletonSizes = useMemo(
    () => ({
      width: containerSizes[size].width,
      height: containerSizes[size].width,
    }),
    [size]
  );

  return (
    <Skeleton visible={isLoading} {...skeletonSizes}>
      <Flex
        justify="center"
        align="center"
        className={classes.container}
        style={styles}
        styles={(theme) => ({
          root: {
            flex: 'none',
            background: 'white',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: '12px',
            width: containerSizes[size].width,
            height: containerSizes[size].height,
          },
        })}
      >
        <Image
          src={cause?.logo?.url}
          isDataLoading={isLoading}
          className={classes.image}
          style={{
            width: imageSizes[size].width,
          }}
        >
          <HandHeartIcon
            className={classes.icon}
            style={{
              position: 'absolute',
              width: iconSizes[size].width,
              height: iconSizes[size].height,
              top: iconSizes[size].top,
              left: iconSizes[size].left,
            }}
          />
        </Image>
      </Flex>
    </Skeleton>
  );
};
